import { hexToRgb, getLuminance, safe } from '../utils/contrast.js';
import { t } from '../utils/i18n.js';
import { iconSvg } from '../utils/icons.js';
import { embedMapsSeguro, enlaceMapsSeguro } from '../utils/maps-url.js';

// ── GOOGLE MAPS ──────────────────────────────────────
export const mapsBlock = (rawProps) => {
    const p = safe(rawProps);
    const variant = p.variant || 'v1';
    const title = p.title || 'Dónde Encontrarnos';
    const subtitle = p.subtitle || 'Visítanos en nuestra ubicación';
    // SIN dirección NO se inventa una (antes el default era 'Madrid, España', que además pintaba
    // un mapa REAL de una ciudad donde el negocio puede no estar). La dirección es un HECHO: la
    // trae la ficha del negocio (utils/negocio.js) o la escribe el cliente aquí mismo. El hueco
    // se señala con un aviso `.editor-only` — emitido SIEMPRE, nunca gateado por `editorMode`,
    // porque `compileSiteHtml` publica con el editor activo (gotcha nº12).
    const address = p.address || '';
    const cleanAddress = address.replace(/<[^>]*>?/gm, '').trim();
    const avisoSinDireccion = cleanAddress ? '' :
        `<span class="maps-empty-notice editor-only">Escribe aquí la dirección de tu negocio</span>`;
    // ENLACE DE GOOGLE MAPS (utils/maps-url.js): si el cliente pegó el de su ficha, el mapa marca el
    // LUGAR exacto (`map_embed`) y el botón abre la FICHA (`map_url`), no una búsqueda de la
    // dirección. Los dos se REVALIDAN aquí (host de Google, https, sin comillas): los props también
    // llegan del JSON del modelo o de un clonado. Sin ninguno, todo sigue saliendo de la dirección.
    const embedPropio = embedMapsSeguro(p.map_embed);
    const enlacePropio = enlaceMapsSeguro(p.map_url);
    const mapSrc = embedPropio || (cleanAddress
        ? `https://maps.google.com/maps?q=${encodeURIComponent(cleanAddress)}&t=&z=14&ie=UTF8&iwloc=&output=embed`
        : '');
    const openHref = enlacePropio
        || (cleanAddress ? `https://www.google.com/maps?q=${encodeURIComponent(cleanAddress)}` : '');

    const isDark = p.bgColor ? getLuminance(...hexToRgb(p.bgColor)) < 0.4 : false;
    const mapFilter = isDark 
        ? "invert(90%) hue-rotate(180deg) contrast(1.05)" 
        : "grayscale(20%) contrast(1.05) saturate(1.1)";

    // --- Tarjeta de info de contacto (compartida) ---
    const contactCard = `
        <div class="maps-card" style="background:var(--card-bg, color-mix(in srgb, var(--text) 7%, transparent));backdrop-filter:blur(24px);-webkit-backdrop-filter:blur(24px);border:var(--card-border, 1px solid color-mix(in srgb, var(--text) 12%, transparent));color:var(--text);padding:28px 36px;border-radius:var(--card-radius, 24px);box-shadow:var(--shadow-card, 0 10px 40px rgba(0,0,0,0.12));display:flex;flex-direction:column;gap:18px;width:100%;max-width:440px;text-align:left;">
            <div style="font-size:calc(1.1rem * var(--heading-scale, var(--u-type-scale, 1)));font-weight:800;display:flex;align-items:center;gap:12px;border-bottom:1px solid color-mix(in srgb, var(--text) 10%, transparent);padding-bottom:14px;">
                <span style="background:var(--accent);color:var(--accent-contrast, #fff);width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;">${iconSvg('mapPin', 18)}</span>
                <span>${t('map_location')}</span>
            </div>
            <div data-field="address" contenteditable="true" style="font-size:calc(1rem * var(--text-scale, 1));opacity:0.85;line-height:1.6;min-height:1.6em;word-break:break-word;">${address}</div>
            ${avisoSinDireccion}
            ${openHref ? `<a href="${openHref}" target="_blank" class="maps-open-link"
               style="display:block;padding:14px 20px;background:var(--accent);color:#fff;text-decoration:none;border-radius:14px;font-weight:700;font-size:calc(0.9rem * var(--text-scale, 1));text-align:center;transition:0.2s;box-shadow:0 4px 16px color-mix(in srgb, var(--accent) 35%, transparent);">${t('map_open')}</a>` : ''}
        </div>`;

    // --- Mapa embed (compartido) ---
    // La interacción (clic en el overlay para desbloquear el mapa, y re-bloqueo al salir
    // con el ratón) la gobierna core/block-actions.js por CLASE (.map-overlay / .maps-wrapper),
    // no handlers on* inline — así el saneo XSS de publicación no rompe el mapa. El wrapper se
    // muestra por defecto (display:block): antes lo revelaba el <object onload>, ya eliminado.
    const mapEmbed = `
        <div class="maps-wrapper" style="border-radius:var(--card-radius, 20px); overflow:hidden; position:relative; height:380px; box-shadow:var(--shadow-card, 0 10px 40px rgba(0,0,0,0.12)); display:block;">
            ${mapSrc ? `<div class="map-overlay" style="position:absolute;inset:0;background:rgba(0,0,0,0.04);z-index:2;display:flex;align-items:center;justify-content:center;transition:0.3s;cursor:pointer;">
                <span style="background:var(--accent);color:var(--accent-contrast, #fff);padding:8px 20px;border-radius:20px;font-size:0.85rem;font-weight:700;white-space:nowrap;box-shadow:0 4px 12px rgba(0,0,0,0.2);backdrop-filter:blur(8px);display:inline-flex;align-items:center;gap:8px;">${iconSvg('pointer', 15)}${t('map_interact')}</span>
            </div>` : ''}
            ${mapSrc ? `<object data="${mapSrc}" type="text/html" width="100%" height="100%"
                    style="border:0; filter:${mapFilter}; pointer-events:none; display:block;">
            </object>` : `<div class="maps-sin-mapa" style="width:100%;height:100%;">
                <button type="button" class="maps-enlace-btn editor-only">${iconSvg('mapPin', 16)}Pegar el enlace de Google Maps</button>
            </div>`}
        </div>`;

    // --- v1 (actual): mapa centrado con info arriba ---
    if (variant === 'v1' || (!variant || variant === '')) {
        return `
    <section id="bloque-maps" class="reveal variant-${variant}"
             style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}';">
        <div style="text-align:center; margin-bottom:40px;">
            <h2 data-field="title" contenteditable="true"
                style="font-size:calc(clamp(2rem,4cqw,3rem) * var(--type-scale, 1)); font-weight:900; margin-bottom:16px;">${title}</h2>
            <p data-field="subtitle" contenteditable="true"
               style="opacity:0.6; font-size:calc(1.1rem * var(--text-scale, 1));">${subtitle}</p>
        </div>
        <div style="display:flex; justify-content:center; margin-bottom:32px;">
            ${contactCard}
        </div>
        ${mapEmbed}
    </section>`;
    }

    // --- v2 ("split"): mapa izquierda (60%) + info derecha (40%) ---
    return `
    <section id="bloque-maps" class="reveal variant-${variant}"
             style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}';">
        <div style="text-align:center; margin-bottom:50px;">
            <h2 data-field="title" contenteditable="true"
                style="font-size:calc(clamp(2rem,4cqw,3rem) * var(--type-scale, 1)); font-weight:900; margin-bottom:16px;">${title}</h2>
            <p data-field="subtitle" contenteditable="true"
               style="opacity:0.6; font-size:calc(1.1rem * var(--text-scale, 1));">${subtitle}</p>
        </div>
        <div class="maps-split">
            <div style="flex: 3; min-width: min(100%, 400px);">
                ${mapEmbed.replace("display:none;", "display:block;")}
            </div>
            <div style="flex: 2; min-width: min(100%, 300px); display: flex; align-items: center;">
                ${contactCard.replace("max-width:440px;", "max-width:100%;")}
            </div>
        </div>
    </section>`;
};
